import React from 'react';
import { useShallow } from 'zustand/react/shallow';
import { PosCheckoutModal } from '../checkout/PosCheckoutModal';
import { PosCheckoutProvider, type PosCheckoutContextValue } from '../contexts/PosCheckoutContext';
import { usePosCheckoutStore } from '../../store/usePosCheckoutStore';
import { usePosCashSessionStore } from '../../store/usePosCashSessionStore';
import type { CartItem } from '../../store/usePosStore';
import type { PosPaymentMethodId } from '../../domain/posTypes';
import type { CartEvaluationResult } from '../../../promotions/public-api';
import type { CartPromoUiStatus } from '../../application/posCartPromoPresentation';

export type PosCheckoutModalContainerProps = {
  cart: CartItem[];
  cartEvaluation: CartEvaluationResult | null;
  promoUiStatus?: CartPromoUiStatus;
  canOpenCashManagementFromPos: boolean;
  allowFiscalSaleMode: boolean;
  currentBranchId: string | null | undefined;
  cashSessionReady: boolean;
  total: number;
  merchandiseTotal?: number;
  tipAmount?: number;
  tipUiEnabled?: boolean;
  tipIntent?: import('../../application/posCheckoutTip').PosTipIntent | null;
  selectTipIntent?: (next: import('../../application/posCheckoutTip').PosTipIntent | null) => void;
  totalIngresado: number;
  vuelto: number;
  saldoPendiente: number;
  isSaving: boolean;
  hasBlockingStockIssue: boolean;
  hasExpiredStock: boolean;
  customerEligibleCustomers: ReturnType<typeof usePosCheckoutStore.getState>['customerOptions'];
  completeSale: () => void;
  updateDraft: (id: string, patch: Record<string, unknown>) => void;
  removeDraft: (id: string) => void;
  addSplitPayment: (methodId?: PosPaymentMethodId) => void;
  handleRequestStockCorrection: () => void;
  handleOpenCashManagementFromCheckout: () => void;
  loadMoreCustomers: (pageIndex: number, loadAll?: boolean) => void;
  onSetCartLineAttribution?: (lineId: string, performedByUserId: string | null, soldByUserId: string | null) => void;
  /** Overlay que se monta dentro del Modal del checkout (ej. confirm de drift). */
  overlayModal?: React.ReactNode;
};

export const PosCheckoutModalContainer = ({
  cart,
  cartEvaluation,
  promoUiStatus,
  canOpenCashManagementFromPos,
  allowFiscalSaleMode,
  currentBranchId,
  cashSessionReady,
  total,
  merchandiseTotal,
  tipAmount,
  tipUiEnabled,
  tipIntent,
  selectTipIntent,
  totalIngresado,
  vuelto,
  saldoPendiente,
  isSaving,
  hasBlockingStockIssue,
  hasExpiredStock,
  customerEligibleCustomers,
  completeSale,
  updateDraft,
  removeDraft,
  addSplitPayment,
  handleRequestStockCorrection,
  handleOpenCashManagementFromCheckout,
  loadMoreCustomers,
  onSetCartLineAttribution,
  overlayModal,
}: PosCheckoutModalContainerProps) => {
  const checkoutStore = usePosCheckoutStore(
    useShallow((s) => ({
      checkoutVisible: s.checkoutVisible,
      checkoutLoading: s.checkoutLoading,
      paymentDrafts: s.paymentDrafts,
      saleFiscalMode: s.saleFiscalMode,
      fiscalBuyerDraft: s.fiscalBuyerDraft,
      paymentMethods: s.paymentMethods,
      customerOptions: s.customerOptions,
      customersTotal: s.customersTotal,
      customerPageIndex: s.customerPageIndex,
      customerPageSize: s.customerPageSize,
      saleCustomerId: s.saleCustomerId,
      stockWarningsLoading: s.stockWarningsLoading,
      stockCanProceed: s.stockCanProceed,
      stockWarningDetails: s.stockWarningDetails,
    })),
  );

  const setSaleFiscalMode = usePosCheckoutStore((s) => s.setSaleFiscalMode);
  const setSaleFiscalModeTouched = usePosCheckoutStore((s) => s.setSaleFiscalModeTouched);
  const setFiscalBuyerDraft = usePosCheckoutStore((s) => s.setFiscalBuyerDraft);
  const setSaleCustomerId = usePosCheckoutStore((s) => s.setSaleCustomerId);
  const dismissCheckout = usePosCheckoutStore((s) => s.dismissCheckout);

  const activeCashSessionId = usePosCashSessionStore((s) => s.activeCashSessionId);
  const cashSessionOpenForCheckout = !cashSessionReady ? null : Boolean(activeCashSessionId);

  const contextValue: PosCheckoutContextValue = {
    cashSessionOpen: cashSessionOpenForCheckout,
    onOpenCashManagement: canOpenCashManagementFromPos
      ? handleOpenCashManagementFromCheckout
      : undefined,
    cashManagementAccessBlockedHint: !canOpenCashManagementFromPos
      ? 'Necesitás permiso de POS o de Caja para abrir la caja. Pedíselo a quien administra.'
      : undefined,
    total,
    merchandiseTotal,
    tipAmount,
    tipUiEnabled,
    tipIntent,
    onSelectTipIntent: selectTipIntent,
    cart,
    cartEvaluation,
    promoUiStatus,
    saleFiscalMode: checkoutStore.saleFiscalMode,
    onSetSaleFiscalMode: (mode) => {
      // El cajero lo eligió a mano: la estrategia 'auto_by_payment' no lo vuelve a pisar.
      setSaleFiscalModeTouched(true);
      setSaleFiscalMode(mode);
    },
    allowFiscalSaleMode,
    stockWarningsLoading: checkoutStore.stockWarningsLoading,
    stockCanProceed: checkoutStore.stockCanProceed,
    stockWarningDetails: checkoutStore.stockWarningDetails,
    hasBlockingStockIssue,
    hasExpiredStock,
    paymentDrafts: checkoutStore.paymentDrafts,
    paymentMethods: checkoutStore.paymentMethods,
    customerEligibleCustomers,
    customersTotal: checkoutStore.customersTotal,
    customerPageIndex: checkoutStore.customerPageIndex,
    customerPageSize: checkoutStore.customerPageSize,
    onLoadMoreCustomers: loadMoreCustomers,
    saleCustomerId: checkoutStore.saleCustomerId,
    setSaleCustomerId,
    totalIngresado,
    saldoPendiente,
    vuelto,
    isSaving,
    onUpdateDraft: updateDraft,
    onRemoveDraft: removeDraft,
    onAddSplitPayment: addSplitPayment,
    onCompleteSale: completeSale,
    onRequestStockCorrection: handleRequestStockCorrection,
    currentBranchId,
    fiscalBuyerDraft: checkoutStore.fiscalBuyerDraft,
    onFiscalBuyerDraftChange: setFiscalBuyerDraft,
    onSetCartLineAttribution,
  };

  const handleClose = isSaving ? () => {} : dismissCheckout;

  return (
    <PosCheckoutProvider value={contextValue}>
      <PosCheckoutModal
        visible={checkoutStore.checkoutVisible}
        checkoutLoading={checkoutStore.checkoutLoading}
        onClose={handleClose}
        onRequestClose={handleClose}
        overlayModal={overlayModal}
      />
    </PosCheckoutProvider>
  );
};
